import { useDispatch, useSelector } from "react-redux";

import { PayableFilter } from "../../types/PayableFilter.types";
import { AppDispatch, RootState } from "../../../../store/store";

import PayableTableService from "../services/PayableTable.service";

import {
    clearPendingSupplierPayables,
    clearPayables,
    setError,
    setLoading,
    setPage,
    setPendingSupplierPayables,
    setPayables,
    setSize,
    setSort,
} from "../store/PayableTable.slice";

import useLoading from "../../../../hooks/useLoading";

import {
    resetPayablePaymentForm,
    setPaymentSummary,
} from "../../payments/store/PayablePayment.slice";

export const usePayableTable = () => {
    const dispatch: AppDispatch = useDispatch();

    const {
        showLoadingIndicator,
        hideLoadingIndicator,
    } = useLoading();

    const {
        list,
        paginator,
        pendingSupplierPayables,
        page,
        size,
        sort,
        loading,
        error,
    } = useSelector(
        (state: RootState) => state.payableTable
    );

    /**
     * 📊 Obtener cuentas por pagar filtradas
     */
    const fetchPayables = async (
        filters: PayableFilter,
        customPage?: number
    ) => {
        try {
            dispatch(setLoading(true));
            showLoadingIndicator();

            const currentPage = customPage ?? page;

            const data =
                await PayableTableService.getFiltered(
                    filters,
                    currentPage,
                    size,
                    sort
                );

            dispatch(setPayables(data));
        } catch (err: any) {
            dispatch(
                setError("Error al cargar cuentas por pagar")
            );
        } finally {
            dispatch(setLoading(false));
            hideLoadingIndicator();
        }
    };

    /**
     * 💰 Obtener resumen de pago
     */
    const fetchPaymentSummary = async (
        payableId: number
    ) => {
        try {
            showLoadingIndicator();

            const data =
                await PayableTableService.getPaymentSummary(
                    payableId
                );

            if (data) {
                dispatch(
                    setPaymentSummary({
                        id: data.id,
                        supplierId: data.supplierId,
                        supplier: data.supplier,
                        number: data.number,
                        total: data.total,
                        paid: data.paid,
                        balance: data.balance,
                        status: data.status || "PENDING",
                    })
                );

                return data;
            }

            dispatch(resetPayablePaymentForm());

            return null;
        } catch (err: any) {
            dispatch(
                setError(
                    "Error al obtener resumen de pago"
                )
            );

            dispatch(resetPayablePaymentForm());

            return null;
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 💰 Obtener cuentas pendientes por proveedor
     */
    const fetchPendingPayablesBySupplier = async (
        supplierId: number,
        search?: string
    ) => {
        try {
            showLoadingIndicator();

            const data =
                await PayableTableService.getPendingBySupplier(
                    supplierId,
                    search
                );

            dispatch(
                setPendingSupplierPayables(
                    data || []
                )
            );
        } catch (err: any) {
            dispatch(
                setError(
                    "Error al obtener cuentas pendientes del proveedor"
                )
            );

            dispatch(
                clearPendingSupplierPayables()
            );
        } finally {
            hideLoadingIndicator();
        }
    };

    /**
     * 📜 Obtener cuentas para historial de pagos
     */
    const fetchPaymentHistoryPayablesBySupplier =
        async (
            supplierId: number,
            search?: string
        ) => {
            try {
                showLoadingIndicator();

                const data =
                    await PayableTableService.getPaymentHistoryBySupplier(
                        supplierId,
                        search
                    );

                dispatch(
                    setPendingSupplierPayables(
                        data || []
                    )
                );
            } catch (err: any) {
                dispatch(
                    setError(
                        "Error al obtener cuentas para historial de pagos"
                    )
                );

                dispatch(
                    clearPendingSupplierPayables()
                );
            } finally {
                hideLoadingIndicator();
            }
        };

    /**
     * 📄 Cambiar página
     */
    const changePage = (
        newPage: number,
        filters: PayableFilter
    ) => {
        dispatch(setPage(newPage));

        fetchPayables(filters, newPage);
    };

    /**
     * 📏 Cambiar tamaño
     */
    const changeSize = (
        newSize: number,
        filters: PayableFilter
    ) => {
        dispatch(setSize(newSize));

        fetchPayables(filters, 0);
    };

    /**
     * 🔄 Cambiar orden
     */
    const changeSort = (
        newSort: string,
        filters: PayableFilter
    ) => {
        dispatch(setSort(newSort));

        fetchPayables(filters, 0);
    };

    const clearFilteredPayables = () => {
        dispatch(clearPayables());
    };

    return {
        // state
        list,
        paginator,
        pendingSupplierPayables,
        page,
        size,
        sort,
        loading,
        error,

        // actions
        fetchPayables,
        fetchPaymentSummary,
        fetchPendingPayablesBySupplier,
        fetchPaymentHistoryPayablesBySupplier,
        changePage,
        changeSize,
        changeSort,
        clearFilteredPayables,
    };
};